import { usersApi } from "@/api/users.api";
import { useAuth } from "@/services/auth.service";
import lassoUrls from "@/utils/lasso-urls";
import * as Sentry from "@sentry/react";
import { useCallback } from "react";

/**
 * Opens Lasso Insights in a new tab.
 *
 * While impersonating, the session came from a login token and no refresh cookie was written - the
 * browser still holds the impersonator's cookie, so Insights would sign in as them instead. In that
 * case we mint a fresh login token for the impersonated user and hand it over as `?lt=`, the same
 * way the Lasso Portal opens Lasso Sales.
 */
export function useOpenInsights() {
  const { user, claims } = useAuth();

  return useCallback(() => {
    const insightsUrl = lassoUrls.getInsightsUrl();

    if (!claims.impersonating) {
      window.open(insightsUrl, "_blank");
      return;
    }

    // Opened up front - a window.open after the request resolves gets caught by popup blockers.
    const tab = window.open("", "_blank");

    usersApi.getImpersonationLoginToken(user).then(
      (token) => {
        if (!token) {
          tab?.close();
          return;
        }
        if (tab) {
          tab.location.href = `${insightsUrl}?lt=${encodeURIComponent(token)}`;
        }
      },
      (error) => {
        tab?.close();
        Sentry.captureException(error);
      },
    );
  }, [claims.impersonating, user]);
}
